import { type FC, useEffect, useMemo, useState, useCallback } from 'react';
import type { Delivery, DriverLocation } from '../../types';
import { DeliveryMap } from '../DeliveryMap';
import { fetchDriverLocationsForDeliveryFromDb } from '../../services/deliveryService';
import StatusBadge from '../StatusBadge';

interface DeliveryDetailsTabProps {
    deliveries: Delivery[];
    orderAddress?: { latitude?: string | number | null; longitude?: string | number | null; address_1?: string } | null;
    orderPin?: string | null;
}

type LocInput = {
    lat?: number | string | null;
    lng?: number | string | null;
    latitude?: number | string | null;
    longitude?: number | string | null;
} | null | undefined;

const getLatLng = (obj: LocInput): { lat: number; lng: number } | null => {
    if (!obj) return null;
    const lat = Number(obj.lat ?? obj.latitude);
    const lng = Number(obj.lng ?? obj.longitude);
    return (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : null;
};

const DeliveryDetailsTab: FC<DeliveryDetailsTabProps> = ({ deliveries, orderAddress, orderPin }) => {
    const [latestLocation,   setLatestLocation]   = useState<DriverLocation | null>(null);
    const [locationLoading,  setLocationLoading]  = useState(false);

    const currentDelivery = useMemo(() =>
            [...deliveries].sort((a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )[0] ?? null,
        [deliveries]);

    const isCancelled = useMemo(() => {
        const s = currentDelivery?.status?.toUpperCase();
        return s === 'ORDER_CANCELLED' || s === 'CANCELLED' || s === 'DELIVERY_FAILED';
    }, [currentDelivery?.status]);

    const isComplete = useMemo(() => {
        return currentDelivery?.status?.toUpperCase() === 'DELIVERY_COMPLETE';
    }, [currentDelivery?.status]);

    const loadLocation = useCallback(async (deliveryId: string) => {
        setLocationLoading(true);
        try {
            const data = await fetchDriverLocationsForDeliveryFromDb(deliveryId);
            if (data.length > 0) {
                const latest = data.reduce((a, b) =>
                    new Date(a.created_at) > new Date(b.created_at) ? a : b
                );
                setLatestLocation(latest as DriverLocation);
            } else {
                setLatestLocation(null);
            }
        } finally {
            setLocationLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!currentDelivery?.id || isCancelled || isComplete) {
            setLatestLocation(null);
            return;
        }
        void loadLocation(String(currentDelivery.id));
    }, [currentDelivery?.id, isCancelled, isComplete, loadLocation]);



    const coords = useMemo(() => ({
        destination: getLatLng(currentDelivery?.destination) ?? getLatLng(orderAddress),
        driver:      getLatLng(latestLocation),
    }), [currentDelivery, latestLocation, orderAddress]);

    if (deliveries.length === 0) {
        return <div className="p-8 text-stone-500">No delivery attempts found.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-base font-semibold text-stone-900">
                                {isCancelled ? 'Last Known Status' : isComplete ? 'Delivery Summary' : 'Live Tracking'}
                            </h2>
                            {!coords.driver && !isCancelled && !isComplete && (
                                <div className="flex items-center gap-1.5">
                                    <div className="h-1.5 w-1.5 rounded-full bg-[#ee2b74] animate-ping" />
                                    <span className="text-[10px] font-medium uppercase tracking-wider text-stone-400">
                                        Locating driver…
                                    </span>
                                </div>
                            )}
                        </div>
                        {orderPin && !isCancelled && !isComplete && (
                            <span className="text-sm font-mono font-bold text-[#ee2b74]">
                                PIN: {orderPin}
                            </span>
                        )}
                    </div>

                    <div className="relative group">
                        {isCancelled ? (
                            <div className="aspect-video w-full bg-stone-100 rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-stone-200 p-8 text-center">
                                <div className="bg-rose-100 p-3 rounded-full mb-3">
                                    <svg className="w-6 h-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-stone-900">Delivery Inactive</h3>
                                <p className="text-sm text-stone-500 max-w-xs mt-1">This delivery was unsuccessful</p>
                            </div>
                        ) : isComplete ? (
                            <div className="aspect-video w-full bg-stone-50 rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-stone-200 p-8 text-center">
                                <div className="bg-[#f0f7e6] p-3 rounded-full mb-3">
                                    <svg className="w-6 h-6 text-[#8dc63f]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="font-semibold text-stone-900">Delivery Complete</h3>
                                <p className="text-sm text-stone-500 max-w-xs mt-1">
                                    {currentDelivery?.delivered_at
                                        ? `Delivered on ${new Date(currentDelivery.delivered_at).toLocaleString()}`
                                        : 'This order has been delivered successfully.'}
                                </p>
                            </div>
                        ) : (
                            <>
                                <DeliveryMap destination={coords.destination} driver={coords.driver} />
                                {!locationLoading && !coords.driver && (
                                    <div className="absolute inset-0 z-[400] flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-2xl">
                                        <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-xl ring-1 ring-black/5">
                                            <div className="w-10 h-10 border-4 border-stone-200 border-t-[#ee2b74] rounded-full animate-spin mb-3" />
                                            <p className="text-sm font-medium text-stone-900">Waiting for driver location</p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    <section className="rounded-xl border border-stone-200 p-4 bg-stone-50/30">
                        <h3 className="text-sm font-semibold text-stone-900 mb-3">Driver & Vehicle</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-stone-500">Name</span>
                                <span className="font-medium text-stone-900">
                                    {currentDelivery?.driver?.driver_name ?? (
                                        isCancelled
                                            ? <span className="text-stone-400 italic font-normal">No driver assigned</span>
                                            : <span className="animate-pulse text-[#ee2b74]">Assigning…</span>
                                    )}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-stone-500">Plate</span>
                                <span className="font-medium text-stone-900">{currentDelivery?.driver?.number_plate ?? '—'}</span>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-xl border border-stone-200 p-4 bg-stone-50/30">
                        <h3 className="text-sm font-semibold text-stone-900 mb-3">Timeline</h3>
                        <div className="space-y-3 text-xs">
                            <div className="flex justify-between">
                                <span className="text-stone-500">Status</span>
                                <StatusBadge status={currentDelivery?.status ?? ''} />
                            </div>
                            <div className="flex justify-between">
                                <span className="text-stone-500">Assigned</span>
                                <span className="text-stone-900">
                                    {currentDelivery?.assigned_at
                                        ? new Date(currentDelivery.assigned_at).toLocaleString()
                                        : '—'}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-stone-500">Completed</span>
                                <span className="text-stone-900">
                                    {currentDelivery?.delivered_at
                                        ? new Date(currentDelivery.delivered_at).toLocaleString()
                                        : isCancelled ? 'N/A' : '—'}
                                </span>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DeliveryDetailsTab;